import React from 'react';
import { useNavigate } from 'react-router-dom';
import TrendingPickCard from './TrendingPickCard';
import detectRegion from '../utils/detectRegion';
import { trendingPath } from '../utils/regions';

/**
 * Shared "Curious what the rest of the flock is bingeing?" promo strip.
 * Used on the public Landing page AND inside the logged-in Dashboard so the
 * copy + visual style stays consistent.
 *
 * Props:
 *   user — optional; passed through to TrendingPickCard so it can pick the
 *          right region for logged-in users.
 *   compact — boolean; when true, removes the outer max-width section padding
 *             (use on Dashboard where the parent already manages spacing).
 *   eyebrow — override the small uppercase label above the headline.
 *   headline — override the H2.
 *   body — override the body paragraph.
 *   ctaLabel — override the CTA button label.
 *   rotatePick — when true, the embedded TrendingPickCard cycles through the
 *                full trending list every 5s (used on Dashboard).
 */
const TrendingPromoSection = ({
  user,
  compact = false,
  eyebrow = 'No sign-up required',
  headline = 'Curious what the rest of the flock is bingeing?',
  body = "Pop over to our trending page and you'll find the shows everyone is talking about this week — pick your patch of the world and we'll show you what's hot, where to stream it, and whether Baarry's flock has given it a thumbs up. No account, no commitment, just a peek over the fence.",
  ctaLabel = 'Explore trending TV →',
  rotatePick = false,
}) => {
  const navigate = useNavigate();
  // Pre-select the user's region so the trending page opens on the right tab.
  // Falls back to navigator.language → timezone → 'global' for guests.
  const targetRegion = detectRegion(user?.region);
  const ctaPath = trendingPath(targetRegion);
  return (
    <section
      className={`trending-promo ${compact ? 'is-compact' : ''}`}
      data-testid="trending-promo"
      aria-label="See what's trending on baad.tv"
    >
      <div className="trending-promo-inner">
        <div className="trending-promo-content">
          <span className="trending-promo-eyebrow">{eyebrow}</span>
          <h2>{headline}</h2>
          <p>{body}</p>
          <button
            type="button"
            className="trending-promo-cta"
            onClick={() => navigate(ctaPath)}
            data-testid="trending-promo-cta"
          >
            {ctaLabel}
          </button>
        </div>
        <div className="trending-promo-pick">
          <TrendingPickCard user={user} rotate={rotatePick} />
        </div>
      </div>

      <style jsx>{`
        .trending-promo {
          margin-top: 4rem;
          position: relative;
        }
        .trending-promo.is-compact {
          margin-top: 1.5rem;
        }
        .trending-promo-inner {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(
              900px 320px at 110% -10%,
              rgba(230, 47, 136, 0.22) 0%,
              rgba(230, 47, 136, 0) 60%
            ),
            linear-gradient(135deg, #15090f 0%, #0d0d0d 100%);
          border: 1px solid #2a1118;
          border-radius: 18px;
          padding: 2.25rem 2.25rem 2.5rem;
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          align-items: center;
        }
        .trending-promo-inner::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: 18px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(230, 47, 136, 0.55), transparent 65%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
        }
        @media (min-width: 900px) {
          .trending-promo-inner {
            grid-template-columns: minmax(0, 1.5fr) minmax(280px, 1fr);
            gap: 2.5rem;
          }
        }
        .trending-promo-content {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          max-width: 720px;
        }
        .trending-promo-eyebrow {
          color: #E62F88;
          font-size: 0.7rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          font-weight: 800;
        }
        .trending-promo-content h2 {
          color: #fff;
          font-size: clamp(1.5rem, 2.6vw, 1.95rem);
          font-weight: 800;
          letter-spacing: -0.01em;
          margin: 0;
          line-height: 1.15;
        }
        .trending-promo-content p {
          color: #c4c4cc;
          font-size: 1rem;
          line-height: 1.6;
          margin: 0 0 0.85rem;
        }
        .trending-promo-cta {
          align-self: flex-start;
          background: #E62F88;
          color: #000;
          border: none;
          padding: 0.85rem 1.5rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          transition: background 0.18s, transform 0.18s;
        }
        .trending-promo-cta:hover {
          background: #ff4f9e;
          transform: translateY(-1px);
        }
        @media (max-width: 720px) {
          .trending-promo {
            margin-top: 2.5rem;
          }
          .trending-promo.is-compact {
            margin-top: 1.25rem;
          }
          .trending-promo-inner {
            padding: 1.5rem;
            gap: 1.5rem;
          }
        }
      `}</style>
    </section>
  );
};

export default TrendingPromoSection;
